/**
 * Segments v1 (B6, ADR-052).
 *
 * Own module per the B3 convention. Wire shapes mirror
 * `server/src/admin/segments.service.ts` field for field. The panel never
 * writes SQL and never sees SQL — a segment is a flat AND-list of
 * `{ field, op, value }` rows over the server's whitelist, and the `fields`
 * metadata the list endpoint returns is the ONLY source of what can be picked.
 */
import { http } from './http.ts';
import { authHeaders } from './auth.ts';
import { MOCK_MODE } from './client.ts';
import type { Currency } from './types.ts';

export type SegmentOp = 'eq' | 'neq' | 'gte' | 'lte';

export interface SegmentPredicate {
  field: string;
  op: SegmentOp;
  value: unknown;
}

export interface SegmentFieldMeta {
  field: string;
  ops: SegmentOp[];
  describe: string;
}

export interface SegmentView {
  id: number;
  name: string;
  predicates: SegmentPredicate[];
  createdBy: number;
  createdAt: string;
  updatedAt: string;
}

export interface SegmentMember {
  userId: number;
  username: string;
  createdAt: string;
}

export interface SegmentPreview {
  total: number;
  sample: SegmentMember[];
}

export interface BatchGrantResult {
  campaignId: number;
  members: number;
  created: number;
  skipped: Record<string, number>;
  failed: number;
}

/** LIVE — `GET /internal/admin/segments`. Segments plus the field whitelist. */
export async function fetchSegments(): Promise<{ segments: SegmentView[]; fields: SegmentFieldMeta[] }> {
  if (MOCK_MODE) return { segments: [], fields: [] };
  return http.get<{ segments: SegmentView[]; fields: SegmentFieldMeta[] }>('/internal/admin/segments');
}

/** LIVE — `POST /internal/admin/segments`. Admin, audited. */
export async function createSegment(name: string, predicates: SegmentPredicate[]): Promise<SegmentView> {
  return http.post<SegmentView>('/internal/admin/segments', { name, predicates });
}

/** LIVE — `PUT /internal/admin/segments/:id`. Admin, audited. */
export async function updateSegment(id: number, name: string, predicates: SegmentPredicate[]): Promise<SegmentView> {
  return http.put<SegmentView>(`/internal/admin/segments/${id}`, { name, predicates });
}

/** LIVE — `DELETE /internal/admin/segments/:id`. Admin, audited. */
export async function deleteSegment(id: number): Promise<void> {
  await http.del<{ deleted: boolean }>(`/internal/admin/segments/${id}`);
}

/** LIVE — `POST /internal/admin/segments/preview`. Stateless: previews the DRAFT. */
export async function previewPredicates(predicates: SegmentPredicate[]): Promise<SegmentPreview> {
  return http.post<SegmentPreview>('/internal/admin/segments/preview', { predicates });
}

/**
 * LIVE — `GET /internal/admin/segments/:id/export.csv` — the moment-in-time
 * member list, as raw CSV text. Fetched with the bearer header (a plain link
 * cannot carry it); the page turns it into a blob download.
 */
export async function exportCsv(id: number): Promise<string> {
  const base = ((import.meta.env['VITE_API_BASE'] as string | undefined) ?? '').replace(/\/$/, '');
  const res = await fetch(`${base}/internal/admin/segments/${id}/export.csv`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`Export failed (${res.status}).`);
  return res.text();
}

/**
 * LIVE — `POST /internal/admin/segments/:id/grant`. Approver-gated; goes
 * THROUGH the promo service so the campaign's own budget cap and guards apply.
 */
export async function grantToSegment(
  id: number,
  campaignId: number,
  currency: Currency,
  amount: string,
): Promise<BatchGrantResult> {
  return http.post<BatchGrantResult>(`/internal/admin/segments/${id}/grant`, { campaignId, currency, amount });
}
